import { clientApi } from '../../configuration';
import getCRUDHooks from '../../utils/services/getCRUDHooks';

import { ProjectLearningActivityStatus } from './constants';

type GetProjectSessionsParams = {
  projectSessionId: number;
};

type MarkAsReadyForReviewParams = {
  projectSessionId: number;
};

type UpdateProjectSessionParams = {
  projectSessionId: number;
  comment: string;
  currentUserId: number;
  presentationVideoUrl: string;
};

type UpdateProjectSessionDecisionParams = {
  projectSessionId: number;
  learningActivityStatus:
    | ProjectLearningActivityStatus.FAILED
    | ProjectLearningActivityStatus.PASSED;
};

type CompleteProjectSessionParams = {
  projectSessionId: number;
  comment: string;
  currentUserId: number;
  learningActivityStatus:
    | ProjectLearningActivityStatus.FAILED
    | ProjectLearningActivityStatus.PASSED;
  presentationVideoUrl: string;
};

const {
  useGet: useProjectSession,
  useUpdate: useProjectSessionUpdate,
  useCreate: useProjectSessionMarkAsReadyForReviewCreation,
} = getCRUDHooks({
  entityName: 'projectSessions',
  withPermissions: true,
  get: ({ projectSessionId }: GetProjectSessionsParams) => {
    if (!projectSessionId) {
      return { body: null };
    }

    return clientApi.getOCClient().helpers.learningActivity.projectSessions.get(projectSessionId);
  },
  update: (params: UpdateProjectSessionParams) => {
    const { projectSessionId, ...body } = params;
    return clientApi
      .getOCClient()
      .helpers.learningActivity.projectSessions.partialUpdate(projectSessionId, body);
  },
  create: ({ projectSessionId }: MarkAsReadyForReviewParams) =>
    clientApi
      .getOCClient()
      .helpers.learningActivity.projectSessions.markAsReadyForReview(projectSessionId),
});

const { useCreate: useProjectSessionFileUpload } = getCRUDHooks({
  entityName: 'projectSessionFileUploads',
  sharedCacheEntities: ['projectSessions'],
  create: ({ projectSessionId, files, onProgress }) =>
    clientApi
      .getOCClient()
      .helpers.learningActivity.projectSessions.upload(projectSessionId, files, onProgress),
});

const { useCreate: useProjectSessionCompletion } = getCRUDHooks({
  entityName: 'completeProjectSession',
  sharedCacheEntities: ['projectSessions'],
  create: (params: CompleteProjectSessionParams) => {
    const { projectSessionId, ...body } = params;
    return clientApi
      .getOCClient()
      .helpers.learningActivity.projectSessions.complete(projectSessionId, body);
  },
});

const { useUpdate: useProjectSessionDecisionUpdate } = getCRUDHooks({
  entityName: 'changeProjectSessionsDecision',
  sharedCacheEntities: ['projectSessions'],
  update: (params: UpdateProjectSessionDecisionParams) => {
    const { projectSessionId, ...body } = params;
    return clientApi
      .getOCClient()
      .helpers.learningActivity.projectSessions.changeDecision(projectSessionId, body);
  },
});

export {
  useProjectSession,
  useProjectSessionFileUpload,
  useProjectSessionUpdate,
  useProjectSessionDecisionUpdate,
  useProjectSessionCompletion,
  useProjectSessionMarkAsReadyForReviewCreation,
};
